import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCart } from '../hooks/useCart';
import { useBranding } from '../hooks/useBranding';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ShoppingCart, Plus, Star } from 'lucide-react';
import ImageLightbox, { ZoomButton } from '../components/ImageLightbox';

function PriceCalc({ tiers, product }) {
  const isPerCase = product?.price_unit === 'per_case';
  // v1.42.0 — start at the item's real floor, not a hardcoded 50,000. The
  // client read that number as the minimum order; for hand-issued tickets the
  // true floor is 20,000.
  const floor = product?.qty_min || (isPerCase ? 1 : (tiers?.[0]?.qty_from || 1000));
  const [qty, setQty] = useState(floor);

  const getUnitPrice = (q) => {
    if (!tiers?.length) return null;
    const tier = [...tiers].reverse().find(t => q >= t.qty_from);
    return tier?.price ?? tiers[0]?.price;
  };

  const quoteRequired = !!product?.quote_required;
  const unitPrice = getUnitPrice(qty);
  const total = unitPrice
    ? (isPerCase ? qty * unitPrice : (qty / 1000) * unitPrice)
    : null;

  return (
    <div className="price-calc">
      <div style={{display:'flex', gap:8, alignItems:'center', marginBottom:10}}>
        <input
          type="number"
          className="form-input"
          style={{width:110}}
          value={qty}
          min={floor}
          step={isPerCase ? 1 : (product?.qty_step || 1000)}
          onChange={e => setQty(parseInt(e.target.value) || 0)}
        />
        <span className="text-muted text-sm">{isPerCase ? 'cases' : 'units'}</span>
      </div>
      {quoteRequired ? (
        <div className="price-label">Toledo Ticket to provide custom pricing</div>
      ) : unitPrice !== null ? (
        <>
          <div className="price-value">${total?.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
          <div className="price-label">
            {isPerCase
              ? `$${unitPrice?.toFixed(2)} per case`
              : `$${unitPrice?.toFixed(2)} per M`}
          </div>
        </>
      ) : (
        <div className="text-muted text-sm">Pricing not available</div>
      )}
    </div>
  );
}

function ProductModal({ product, onClose, onAdd }) {
  const { defaultProductImage } = useBranding();
  const { user: viewer } = useAuth();
  // A Toledo Ticket staff account has no customer, and almost every price in
  // this catalog is customer-specific. So staff see "no price" on items that
  // are priced perfectly well for the customer who owns them. That looked like
  // a pricing bug to the client and cost a round trip to diagnose; the screen
  // now says which it is instead of leaving them to guess.
  const staffWithoutCustomer = !!viewer && !viewer.customer_id
    && String(viewer.role || '').startsWith('ttc_');
  const modalImg = product.image_url || defaultProductImage || '/default-ticket.svg';
  const [zoom, setZoom] = useState(false);
  const [detail, setDetail] = useState(null);
  const [qty, setQty] = useState(1);
  const [attrs, setAttrs] = useState({});
  const [priceInfo, setPriceInfo] = useState(null);
  const [prefill, setPrefill] = useState(null);   // {order_number, ordered_at} | {none:true}

  const isPerCase = product.price_unit === 'per_case';
  // Prefer the server's view (it also falls back to the lowest priced tier),
  // so the box, the Add-to-Cart check and the submit check cannot disagree.
  const qtyMin  = detail?.qty_rules?.min ?? (product.qty_min || (isPerCase ? 1 : 1000));
  const qtyMax  = detail?.qty_rules?.max ?? (product.qty_max || null);
  const qtyStep = detail?.qty_rules?.step ?? (product.qty_step || null);

  useEffect(() => {
    api.get(`/api/products/${product.id}`).then(r => {
      setDetail(r.data);
      // v1.42.0 — the server works out the floor (explicit qty_min, else the
      // lowest priced quantity) so the box and the submit check agree.
      setQty(r.data.qty_rules?.default || product.qty_min || (isPerCase ? 1 : 1000));
    });
  }, [product.id]);

  // Conditional visibility (visible_when) + dependent options (depends_on).
  const isVisible = (attr) => {
    if (!attr.visible_when) return true;
    return (attr.visible_when.in || []).includes(attrs[attr.visible_when.code]);
  };
  const optionsFor = (attr) => {
    if (attr.depends_on && attr.options_by) {
      return attr.options_by[attrs[attr.depends_on]] || attr.options_by._override || [];
    }
    return attr.options || [];
  };
  const visibleAttrs = (detail?.attributes || []).filter(isVisible);

  // ── Reorder with changes ────────────────────────────────────────────────
  // Answering a "Type of Order" question with a reorder-style value copies the
  // answers from this customer's last order of the item, all still editable.
  const isTypeOfOrder = (attr) =>
    /(^|_)(type_of_order|order_type)/.test(attr.code || '') ||
    /type of order|order type/i.test(attr.label || '');
  const meansReorder = (v) => /re-?order|repeat|same as (last|previous)|previous order/i.test(String(v || ''));

  const applyAnswer = (attr, value) => {
    setAttrs(a => ({ ...a, [attr.code]: value }));
    if (!isTypeOfOrder(attr)) return;
    if (!meansReorder(value)) { setPrefill(null); return; }

    api.get(`/api/products/${product.id}/last-answers`).then(r => {
      if (!r.data.found) { setPrefill({ none: true }); return; }
      const codes = new Set((detail?.attributes || []).map(x => x.code));
      const copied = {};
      Object.entries(r.data.answers || {}).forEach(([k, v]) => {
        if (codes.has(k) && k !== attr.code) copied[k] = v;   // keep the answer just given
      });
      setAttrs(a => ({ ...a, ...copied, [attr.code]: value }));
      if (r.data.quantity) {
        const q = Math.min(Math.max(r.data.quantity, qtyMin), qtyMax || r.data.quantity);
        setQty(q);
      }
      setPrefill({ order_number: r.data.order_number, ordered_at: r.data.ordered_at });
    }).catch(() => setPrefill({ none: true }));
  };

  const clearPrefill = () => {
    const keep = {};
    (detail?.attributes || []).forEach(x => {
      if (isTypeOfOrder(x) && attrs[x.code]) keep[x.code] = attrs[x.code];
    });
    setAttrs(keep);
    setPrefill(null);
  };

  // Live price from the server (debounced) — base tier + any answer-driven adders.
  useEffect(() => {
    if (!detail || !detail.tiers?.length) { setPriceInfo(null); return; }
    const t = setTimeout(() => {
      api.post(`/api/products/${product.id}/price`, { qty, answers: attrs })
        .then(r => setPriceInfo(r.data)).catch(() => setPriceInfo(null));
    }, 300);
    return () => clearTimeout(t);
  }, [qty, attrs, detail, product.id]);

  const total = priceInfo?.total ?? 0;
  const unitPrice = priceInfo?.unit_price ?? null;
  const additional = priceInfo?.additional_charges ?? 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{product.name}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {!detail ? <div className="loading">Loading…</div> : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 14 }}>
              <img src={modalImg} alt={product.name} onClick={() => setZoom(true)} title="Click to enlarge"
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, objectFit: 'contain', cursor: 'zoom-in' }} />
            </div>
            {zoom && <ImageLightbox src={modalImg} alt={product.name} onClose={() => setZoom(false)} />}
            {isPerCase && detail.product.case_qty_label && (
              <div className="alert alert-info" style={{marginBottom:12}}>
                1 case = {detail.product.case_qty_label}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{product.qty_unit_label || (isPerCase ? 'Cases' : 'Quantity (units)')}
                {qtyMax ? <span className="text-sm text-muted"> ({qtyMin.toLocaleString()}–{qtyMax.toLocaleString()})</span> : null}
              </label>
              <input type="number" className="form-input" value={qty}
                onChange={e => setQty(parseInt(e.target.value) || 0)}
                min={qtyMin} max={qtyMax || undefined} step={isPerCase ? 1 : (qtyStep || 1000)} />
              {/* Say the rule up front. Finding out at Add to Cart that the
                  quantity had to be a multiple of 4,000 is a worse way to
                  learn it. */}
              {qtyStep && (
                <p className="form-hint">
                  Ordered in multiples of {qtyStep.toLocaleString()} {isPerCase ? 'cases' : 'tickets'},
                  starting at {qtyMin.toLocaleString()}.
                </p>
              )}
            </div>

            {prefill && (
              <div className="alert alert-info" style={{ marginBottom: 12 }}>
                {prefill.none
                  ? 'No previous order of this item to copy — fill the form in below.'
                  : <>
                      Copied from order <strong>{prefill.order_number}</strong>
                      {prefill.ordered_at ? ` (${new Date(prefill.ordered_at).toLocaleDateString()})` : ''}.
                      Change anything you need.
                      <button className="link-btn" style={{ marginLeft: 8 }} onClick={clearPrefill}>Start blank instead</button>
                    </>}
              </div>
            )}

            {visibleAttrs.map(attr => (
              <div className="form-group" key={attr.id}>
                <label className="form-label">
                  {attr.label}
                  {attr.required ? <span className="required">*</span> : null}
                </label>
                {attr.input_type === 'select' ? (
                  <select className="form-select" value={attrs[attr.code] || ''}
                    onChange={e => applyAnswer(attr, e.target.value)}>
                    <option value="">— Select —</option>
                    {optionsFor(attr).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : attr.input_type === 'textarea' ? (
                  <textarea className="form-textarea" value={attrs[attr.code] || ''}
                    onChange={e => applyAnswer(attr, e.target.value)} />
                ) : attr.input_type === 'number' ? (
                  <input type="number" className="form-input" value={attrs[attr.code] || ''}
                    onChange={e => applyAnswer(attr, e.target.value)} />
                ) : (
                  <input type="text" className="form-input" value={attrs[attr.code] || ''}
                    onChange={e => applyAnswer(attr, e.target.value)} />
                )}
              </div>
            ))}

            {priceInfo?.quote_required ? (
              <div className="alert alert-info" style={{marginTop:10}}>
                <strong>Toledo Ticket to provide custom pricing.</strong><br />
                This item has no contract price. Your order will be sent through and
                Toledo Ticket will price it before anything is made.
              </div>
            ) : detail.tiers.length > 0 && priceInfo ? (
              <div className="price-calc">
                <div className="price-value">${total.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                <div className="price-label">
                  {isPerCase
                    ? `${qty.toLocaleString()} ${(product.qty_unit_label || 'case').toLowerCase().replace(/s$/,'')}${qty !== 1 ? 's' : ''} × $${(unitPrice||0).toFixed(2)}/${(product.qty_unit_label || 'case').toLowerCase().replace(/s$/,'')}`
                    : `${qty.toLocaleString()} units × $${(unitPrice||0).toFixed(2)}/M`}
                </div>
                {/* Itemised, so the customer can see what each option costs
                    rather than a lump sum they have to take on trust. */}
                {(priceInfo?.adders || []).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, textAlign: 'left' }}>
                    {priceInfo.adders.map((a, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span className="text-muted">
                          {a.label}
                          {a.unit !== 'flat' && a.quantity != null && a.unit_rate != null
                            ? ` (${Number(a.quantity).toLocaleString()} × $${Number(a.unit_rate).toFixed(2)})` : ''}
                        </span>
                        <span>${Number(a.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(priceInfo?.unpriced || []).length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--brand-red)', textAlign: 'left' }}>
                    {priceInfo.unpriced.map(u => u.label).join(', ')} — Toledo Ticket will confirm the price.
                  </div>
                )}
              </div>
            ) : detail.tiers.length === 0 ? (
              staffWithoutCustomer ? (
                <div className="alert alert-info" style={{marginTop:10}}>
                  <strong>No price shown because you are signed in as Toledo Ticket staff.</strong>
                  <div style={{marginTop:6}}>
                    Prices here are set per customer, and a staff account is not one, so
                    customer pricing cannot be looked up. To see what a customer sees, go to
                    Admin → Users and use Impersonate on one of their users. This item may well
                    be priced correctly for them.
                  </div>
                </div>
              ) : (
                <div className="alert alert-info" style={{marginTop:10}}>Pricing not available — contact Toledo Ticket for a quote.</div>
              )
            ) : null}
          </>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={() => {
            if (qty < qtyMin || (qtyMax && qty > qtyMax)) {
              return toast.error(qtyMax ? `Quantity must be between ${qtyMin.toLocaleString()} and ${qtyMax.toLocaleString()}` : `Minimum quantity is ${qtyMin.toLocaleString()}`);
            }
            // v1.42.0 — the case-increment rule, checked here as well as at
            // submit. Name the two valid quantities either side, so the fix is
            // obvious rather than arithmetic.
            if (qtyStep && (qty - qtyMin) % qtyStep !== 0) {
              const below = qtyMin + Math.floor((qty - qtyMin) / qtyStep) * qtyStep;
              return toast.error(
                `${product.name} is ordered in multiples of ${qtyStep.toLocaleString()}. `
                + `Try ${below.toLocaleString()} or ${(below + qtyStep).toLocaleString()}.`);
            }
            for (const a of visibleAttrs) {
              if (a.required && !String(attrs[a.code] || '').trim()) return toast.error(`${a.label} is required`);
            }
            if (!priceInfo?.quote_required && detail.tiers.length > 0 && !priceInfo) {
              return toast.error('No pricing available');
            }
            const clean = {};
            visibleAttrs.forEach(a => { if (attrs[a.code] !== undefined && attrs[a.code] !== '') clean[a.code] = attrs[a.code]; });
            onAdd(product, qty, clean, unitPrice || 0);
            onClose();
          }}>
            <Plus size={15} /> Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}



export default function CatalogPage() {
  const { defaultProductImage } = useBranding();
  const imgOf = (p) => p.image_url || defaultProductImage || '/default-ticket.svg';
  const { user } = useAuth();
  const { addItem } = useCart();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState(null);
  const [favIds, setFavIds] = useState([]);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [frequentIds, setFrequentIds] = useState([]);

  useEffect(() => {
    api.get('/api/products').then(r => {
      setProducts(r.data.products);
      setLoading(false);
    });
    api.get('/api/products/favorites').then(r => setFavIds(r.data.product_ids || [])).catch(() => {});
    api.get('/api/products/frequent').then(r => setFrequentIds(r.data.product_ids || [])).catch(() => {});
  }, []);

  const isFav = (id) => favIds.includes(id);
  const toggleFav = async (id, e) => {
    if (e) e.stopPropagation();
    const currently = isFav(id);
    setFavIds(currently ? favIds.filter(x => x !== id) : [id, ...favIds]);
    try {
      if (currently) await api.delete(`/api/products/favorites/${id}`);
      else await api.post('/api/products/favorites', { product_id: id });
    } catch (_) { /* revert on failure */ setFavIds(currently ? [id, ...favIds] : favIds.filter(x => x !== id)); }
  };

  // Quick order = pinned first, then frequent (dedup), limited — only items still visible.
  const byId = new Map(products.map(p => [p.id, p]));
  const quickList = [...favIds, ...frequentIds.filter(id => !favIds.includes(id))]
    .map(id => byId.get(id)).filter(Boolean).slice(0, 8);

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    return (!search || p.name.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q))
      && (!category || p.category === category);
  });

  const handleAdd = (product, qty, attrs, pricePerM) => {
    addItem(product, qty, attrs, pricePerM);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <>
      <div className="topbar">
        <h2>Product Catalog</h2>
        <button className="btn btn-gold btn-sm" onClick={() => nav('/cart')}>
          <ShoppingCart size={15} /> View Cart
        </button>
      </div>
      <div className="page">
        {quickList.length > 0 && (
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-header"><h3>Quick order</h3></div>
            <p className="form-hint" style={{ marginTop: 0 }}>Your pinned and most-ordered items. Tap one to add it.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {quickList.map(p => (
                <div key={p.id} onClick={() => setSelected(p)}
                  style={{ position: 'relative', width: 150, cursor: 'pointer', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button onClick={(e) => toggleFav(p.id, e)} title={isFav(p.id) ? 'Unpin' : 'Pin'}
                    style={{ position: 'absolute', top: 6, right: 6, background: 'none', border: 'none', cursor: 'pointer', color: isFav(p.id) ? 'var(--brand-gold, #EAAB00)' : 'var(--gray-300)' }}>
                    <Star size={16} fill={isFav(p.id) ? 'currentColor' : 'none'} />
                  </button>
                  <div style={{ position: 'relative', width: '100%', height: 70, borderRadius: 6, overflow: 'hidden', background: 'var(--gray-100)' }}>
                    <img src={imgOf(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <ZoomButton onZoom={() => setZoomSrc(imgOf(p))} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{p.name}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="filter-bar">
          <input className="form-input" placeholder="Search products…" value={search}
            onChange={e => setSearch(e.target.value)} style={{width:220}} />
          <select className="form-select" value={category} onChange={e => setCategory(e.target.value)} style={{width:200}}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-muted text-sm">{filtered.length} products</span>
        </div>

        {loading ? <div className="loading">Loading…</div> : (
          <div className="card" style={{padding:0}}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th>Case Contents</th>
                    <th>SKU</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div onClick={() => setSelected(p)} title="View / order"
                          style={{ width: 44, height: 44, borderRadius: 6, overflow: 'hidden', background: 'var(--gray-100)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={imgOf(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button onClick={(e) => toggleFav(p.id, e)} title={isFav(p.id) ? 'Unpin' : 'Pin to quick order'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: isFav(p.id) ? 'var(--brand-gold, #EAAB00)' : 'var(--gray-300)' }}>
                            <Star size={15} fill={isFav(p.id) ? 'currentColor' : 'none'} />
                          </button>
                          <div>
                            <strong>{p.name}</strong>
                            {p.description && <div className="text-sm text-muted">{p.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="text-sm">{p.category}</td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 100,
                          background: p.price_unit === 'per_case' ? 'var(--amber-bg)' : 'var(--blue-bg)',
                          color: p.price_unit === 'per_case' ? 'var(--amber)' : 'var(--blue)',
                        }}>
                          {p.price_unit === 'per_case' ? 'Per Case' : 'Per M'}
                        </span>
                      </td>
                      <td className="text-sm text-muted">{p.case_qty_label || '—'}</td>
                      <td className="text-muted text-sm" style={{fontFamily:'monospace'}}>{p.sku}</td>
                      <td>
                        <button className="btn btn-primary btn-sm" onClick={() => setSelected(p)}>
                          <Plus size={14} /> Add to Order
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selected && (
          <ProductModal
            product={selected}
            onClose={() => setSelected(null)}
            onAdd={handleAdd}
          />
        )}
        {zoomSrc && <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />}
      </div>
    </>
  );
}
